import React from 'react';
import PropTypes from 'prop-types';
import { spacingProps, Box } from '@telus-uds/components-base';
import throttle from 'lodash.throttle';
import StickyWrapper from './styles';
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
function createObserver(ref, callback) {
  let workWithViewport = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : true;
  const observer = new IntersectionObserver(_ref => {
    let [e] = _ref;
    const isNotVisibleInViewport = e.intersectionRatio < 1;
    let correctYPosition = true;
    if (workWithViewport) {
      const {
        y
      } = e.boundingClientRect;
      correctYPosition = y < 0;
    }
    callback(isNotVisibleInViewport && correctYPosition);
  });
  observer.observe(ref.current);
  return observer;
}
const Sticky = _ref2 => {
  let {
    children,
    variant = 'default',
    vertical = {
      xs: 0,
      sm: 0,
      lg: 0,
      xl: 0
    },
    horizontal = {
      xs: 0,
      sm: 0,
      lg: 0,
      xl: 0
    },
    workWithViewport = true,
    position = 'top',
    shadow = true,
    onStick = () => {},
    throttleTime = 100,
    throttleConfig = {
      trailing: true
    }
  } = _ref2;
  // Position "bottom" does not support the "hidden" variant.
  if (variant === 'hidden' && position === 'bottom') {
    // eslint-disable-next-line no-param-reassign
    variant = 'default';
  }
  const sentinelTopRef = React.useRef();
  const sentinelBottomRef = React.useRef();
  const stickyWrapperRef = React.useRef();
  const [observer, setObserver] = React.useState();
  const onStickThrottled = throttle(onStick, throttleTime, throttleConfig);
  const sentinelPositionRef = position === 'top' ? sentinelTopRef : sentinelBottomRef;
  React.useEffect(() => {
    if (observer) {
      observer.disconnect();
    }
    if (variant === 'default' || variant === 'hidden') {
      setObserver(createObserver(sentinelPositionRef, intersected => {
        stickyWrapperRef.current?.classList.toggle('sticky-mode', intersected);
        stickyWrapperRef.current?.classList.toggle(position, intersected);
        if (intersected) {
          stickyWrapperRef.current?.classList.remove('pristine');
        }
        onStickThrottled(intersected);
      }, workWithViewport));
    }
    if (variant === 'alternate') {
      setObserver(createObserver(sentinelBottomRef, intersected => {
        stickyWrapperRef.current?.classList.toggle('sticky-mode', intersected);
        stickyWrapperRef.current?.classList.toggle('bottom', intersected);
        onStickThrottled(intersected);
      }, workWithViewport));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [variant]);
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx("div", {
      className: "sentinelTop",
      ref: sentinelTopRef
    }), /*#__PURE__*/_jsx(StickyWrapper, {
      className: `${variant} ${variant === 'hidden' ? 'pristine' : ''}`,
      ref: stickyWrapperRef,
      shadow: shadow,
      position: position,
      children: /*#__PURE__*/_jsx("div", {
        className: "sticky-element",
        children: /*#__PURE__*/_jsx(Box, {
          vertical: vertical,
          horizontal: horizontal,
          children: children
        })
      })
    }), /*#__PURE__*/_jsx("div", {
      className: "sentinelBottom",
      ref: sentinelBottomRef
    })]
  });
};
Sticky.propTypes = {
  /**
   * Sets top and bottom padding using the theme's spacing scale.
   *
   */
  vertical: spacingProps.types.spacingValue,
  /**
   * Sets left and right padding using the theme's spacing scale.
   *
   */
  horizontal: spacingProps.types.spacingValue,
  /**
   * Whether the sticky should wait to cross the viewport to stick or not
   * Defaults to true. Set to false for nested container (using overflow)
   */
  workWithViewport: PropTypes.bool,
  /**
   * Sticky accepts any content as children.
   */
  children: PropTypes.node.isRequired,
  variant: PropTypes.oneOf(['default', 'alternate', 'hidden']),
  /**
   * Sets whether sticky component is stuck to top or bottom of page.
   * Note: Position "bottom" does not support the "hidden" variant.
   */
  position: PropTypes.oneOf(['top', 'bottom']),
  /**
   * Adds shadow to sticky component.
   */
  shadow: PropTypes.bool,
  /**
   * Callback when sticky is stuck to top or bottom of page.
   */
  onStick: PropTypes.func,
  /**
   * Time in milliseconds to throttle the onStick callback.
   */
  throttleTime: PropTypes.number,
  /**
   * Throttle configuration.
   */
  throttleConfig: PropTypes.object
};
export default Sticky;